
import {
  computed,
  defineComponent, onMounted,
  Ref,
  ref,
  watch,
  onBeforeMount,
} from 'vue';

import { useStore } from '@/store';
import Tooltip from 'primevue/tooltip';
import InputText from 'primevue/inputtext';
import Panel from 'primevue/panel';
import { useI18n } from 'vue-i18n';
import variables from '@/components/template-builder/utils/variables-list';
import {
  hideLeftToolbar,
  markTemplateAsEdited,
  TemplateEditorState as state,
  togglePanel,
  updateTemplateInformations,
  setTemplateConfigurationKey,
  isGamePrizeTemplate,
} from '@/composables/template-editor/TemplateEditor';
import { TemplateTypeEnum } from '@/composables/shop/Templates';
import {
  StringMap,
  TemplateInformations,
  TemplateParentTypeEnum,
  SelectedSyncElement,
  ElementPartTypeEnum,
} from '@/types';
import { getShopDefaultLang, Lang, UserState } from '@/composables/User';
import { getTemplateParentTypeByType } from '@/components/template-builder/utils/helpers';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import {
  loadSenderAddresses,
  loadSenderNames,
  searchValuesForAutocompletion,
} from '@/composables/autoCompletionValuesMedia';
import getShopsDomains from '@/composables/shop/ShopsSendDomains';
import { OperatorType } from '@/types/generated-types/graphql';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'InformationPanel',

  components: {
    FieldErrors,
    LocalizedTextInput,
    InputText,
    Panel,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  setup(props: { formValidation: any }) {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();

    const selectedSyncElement = computed<SelectedSyncElement>(() => store.getters['liveEditor/getSelectedSyncElement']);

    const name: Ref<string> = ref(!selectedSyncElement.value ? state.template?.informations.name ?? '' : selectedSyncElement.value.elementName ?? '');
    const languages: string[] = UserState.activeShop?.langs.map((language: Lang) => language.id) ?? [];
    const defaultLang: string = state.template?.informations.lang ?? '';
    const defaultLanguage: string = getShopDefaultLang();
    const subject: Ref<StringMap> = ref({});
    const previewText: Ref<StringMap> = ref({});
    const fromEmail: Ref<StringMap> = ref({});
    const fromName: Ref<StringMap> = ref({});

    const formValidationErrors = ref({});
    const componentFieldErrorsKey = ref(0);

    languages.forEach((language: string) => {
      if (getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.EMAIL) {
        if (state.template?.informations.subject) {
          subject.value[language] = state.template?.informations.subject[language] ?? state.template?.informations.subject[defaultLanguage] ?? '';
        }

        if (state.template?.informations.previewText) {
          previewText.value[language] = state.template?.informations.previewText[language] ?? state.template?.informations.previewText[defaultLanguage] ?? '';
        }

        if (state.template?.informations.fromEmail) {
          fromEmail.value[language] = state.template?.informations.fromEmail[language] ?? state.template?.informations.fromEmail[defaultLanguage] ?? '';
        }

        if (state.template?.informations.fromName) {
          fromName.value[language] = state.template?.informations.fromName[language] ?? state.template?.informations.fromName[defaultLanguage] ?? '';
        }
      } else if (getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.SMS && state.template?.informations.fromName) {
        fromName.value[language] = state.template?.informations.fromName[language] ?? state.template?.informations.fromName[defaultLanguage] ?? '';
      }
    });

    const isEmailTemplate = computed(() => getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.EMAIL);
    const isSmsTemplate = computed(() => getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.SMS);
    const isTeaserTemplate = computed(() => state.template?.type === TemplateTypeEnum.TEASER);
    const isGamePrize = computed(() => isGamePrizeTemplate());

    const panelTitle = computed(() => {
      if (isTeaserTemplate.value) {
        return t('templateBuilder.teaser.displaySettings');
      }
      return t('templateBuilder.toolbar.tooltip.information');
    });

    // Teaser display mode options
    const displayModeOptions = computed(() => [
      {
        value: 'after_close',
        label: t('templateBuilder.teaser.displayMode.afterClose'),
        description: null,
      },
      {
        value: 'before_display',
        label: t('templateBuilder.teaser.displayMode.beforeDisplay'),
        description: t('templateBuilder.teaser.displayMode.beforeDisplayDesc'),
      },
      {
        value: 'both',
        label: t('templateBuilder.teaser.displayMode.both'),
        description: t('templateBuilder.teaser.displayMode.bothDesc'),
      },
    ]);

    const displayMode = ref<string>(state.template?.configuration?.displayMode || 'after_close');

    const setDisplayMode = (mode: string) => {
      displayMode.value = mode;
      setTemplateConfigurationKey('displayMode', mode);
      markTemplateAsEdited();
    };

    onBeforeMount(() => {
      if (isTeaserTemplate.value && !state.template?.configuration?.displayMode) {
        setTemplateConfigurationKey('displayMode', 'after_close');
      }
    });

    watch([name, subject.value, previewText.value, fromEmail.value, fromName.value], () => {
      if (!selectedSyncElement.value) {
        const informations: TemplateInformations = {
          name: name.value,
          lang: defaultLang,
        };

        if (getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.EMAIL) {
          informations.subject = subject.value;
          informations.previewText = previewText.value;
          informations.fromEmail = fromEmail.value;
          informations.fromName = fromName.value;
        } else if (getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.SMS) {
          informations.fromName = fromName.value;
        }

        updateTemplateInformations(informations);
        markTemplateAsEdited();
      } else {
        store.commit('liveEditor/setSelectedSyncElementName', name.value);
      }
    });

    const goToDomainPage = () => {
      const routePath = router.resolve({ path: '/shop/send-domains' }).href;
      window.open(routePath, '_blank');
      togglePanel('informationPanel');
    };

    const myDataAutoCompletion: Ref<any> = ref({
      senderNames: [],
      senderAddresses: [],
      domainsOfShop: [],
    });

    onMounted(async () => {
      myDataAutoCompletion.value.senderNames = await loadSenderNames(state.template?.type ?? 'email');

      if (state.template?.type === 'email') {
        myDataAutoCompletion.value.senderAddresses = await loadSenderAddresses();
        myDataAutoCompletion.value.domainsOfShop = await getShopsDomains(0, [{
          field: 'domain_verified',
          operator: OperatorType.Equals,
          value: true,
        }]);
      }

      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const informationNameInputLabel = computed(() => {

      if (selectedSyncElement.value) {
        if (selectedSyncElement.value.elementPartType === ElementPartTypeEnum.STYLES) {
          return t('templateBuilder.fields.syncElementNameStyles')
        }
        return t('templateBuilder.fields.syncElementName');
      }
      return t('templateBuilder.fields.templateName', null);
    });

    return {
      t,
      name,
      subject,
      previewText,
      fromEmail,
      fromName,
      variables,
      isEmailTemplate,
      isSmsTemplate,
      isTeaserTemplate,
      panelTitle,
      displayModeOptions,
      displayMode,
      setDisplayMode,
      hideLeftToolbar,
      isGamePrize,
      formValidationErrors,
      componentFieldErrorsKey,
      myDataAutoCompletion,
      searchValuesForAutocompletion,
      goToDomainPage,
      selectedSyncElement,
      informationNameInputLabel,
    };
  },
});
